import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';
import type { Board, BoardRoute, BoardStop } from './types';

/**
 * The live board.
 *
 * Opens an event stream, takes the full board from its first frame, then patches
 * that state in place as events arrive. Re-fetching the whole board on every
 * technician arrival would be twenty requests a minute at this fleet size and
 * would make the map flicker; patching keeps the view stable and the scroll
 * position intact while numbers move underneath.
 */

export type Connection = 'connecting' | 'live' | 'error';

/** How a route build ended, as the live feed reports it. */
export type OptimizeEvent =
  | {
      type: 'finished';
      requestId: string;
      stats: { routes: number; assigned: number; unassigned: number; totalDistanceMeters: number; elapsedMs: number } | null;
    }
  | { type: 'failed'; requestId: string; error: string };

interface ProjectedStopPayload {
  id: string;
  arrival: number;
  departure: number;
  deltaSeconds: number;
  windowRisk: BoardStop['windowRisk'];
  status: BoardStop['status'];
  isProjected: boolean;
}

interface RouteProjection {
  routeId: string;
  stops: ProjectedStopPayload[];
  travelFactor: number;
  projectedRouteEnd: number;
  routeDeltaSeconds: number;
}

export interface LiveBoard {
  board: Board | null;
  connection: Connection;
  /** Rises every time a projection lands, so views can flash what changed. */
  revision: number;
  lastEventAt: number | null;
  refresh: () => Promise<void>;
}

export function useLiveBoard(
  territoryId: string | null,
  date: string,
  onOptimize?: (event: OptimizeEvent) => void,
): LiveBoard {
  /* In a ref, so a new handler each render does not reopen the stream. */
  const onOptimizeRef = useRef(onOptimize);
  onOptimizeRef.current = onOptimize;
  const [board, setBoard] = useState<Board | null>(null);
  const [connection, setConnection] = useState<Connection>('connecting');
  const [revision, setRevision] = useState(0);
  const [lastEventAt, setLastEventAt] = useState<number | null>(null);
  const sourceRef = useRef<EventSource | null>(null);

  const refresh = useCallback(async () => {
    if (!territoryId) return;
    const next = await api.board(territoryId, date);
    setBoard(next);
    setRevision((r) => r + 1);
  }, [territoryId, date]);

  useEffect(() => {
    if (!territoryId) return;

    setConnection('connecting');
    setBoard(null);

    const source = new EventSource(api.streamUrl(territoryId, date));
    sourceRef.current = source;

    source.addEventListener('board', (event) => {
      setBoard(JSON.parse((event as MessageEvent).data) as Board);
      setConnection('live');
      setRevision((r) => r + 1);
    });

    source.addEventListener('route.projected', (event) => {
      const payload = JSON.parse((event as MessageEvent).data) as { route: RouteProjection };
      applyProjection(setBoard, payload.route);
      setLastEventAt(Date.now());
      setRevision((r) => r + 1);
    });

    // A plan change reshapes routes entirely, so take a fresh board.
    const reload = () => {
      void refresh();
    };
    source.addEventListener('plan.updated', reload);

    /*
     * A driver checking in or out, or taking a break, changes no stop -- so no
     * projection follows it -- but the route row shows it. Those few events a
     * day each fetch a fresh board; stop events arrive as projections above.
     */
    source.addEventListener('stop.updated', (event) => {
      const data = JSON.parse((event as MessageEvent).data) as { stop?: { eventType?: string } };
      if (['shift_start', 'shift_end', 'break_start', 'break_end'].includes(data.stop?.eventType ?? '')) {
        setLastEventAt(Date.now());
        reload();
      }
    });

    /*
     * An order created, edited or removed -- in the console, through /v1, or
     * through the compat API.
     *
     * A new position is patched in place: the geocoder sends one per order it
     * resolves, and those are the bulk of these. Anything else can add an
     * order to the day or take one off it, which only the server can decide
     * (it is the server that knows which date an order falls on and whether a
     * plan holds it), so the board is fetched again -- once per burst, not
     * once per order, so an import of eighty orders is one request.
     */
    let orderTimer: number | undefined;
    source.addEventListener('order.updated', (event) => {
      const data = JSON.parse((event as MessageEvent).data) as { order?: unknown };
      const o = data.order as { id?: unknown; lat?: unknown; lng?: unknown } | undefined;
      const onlyPosition =
        o !== undefined &&
        !Array.isArray(o) &&
        typeof o.id === 'string' &&
        typeof o.lat === 'number' &&
        typeof o.lng === 'number' &&
        Object.keys(o).length === 3;
      setLastEventAt(Date.now());
      if (onlyPosition) {
        const { id, lat, lng } = o as { id: string; lat: number; lng: number };
        setBoard((current) => {
          if (!current) return current;
          return {
            ...current,
            unassigned: current.unassigned.map((u) => (u.id === id ? { ...u, lat, lng } : u)),
            routes: current.routes.map((r) =>
              r.stops.some((s) => s.orderId === id)
                ? { ...r, stops: r.stops.map((s) => (s.orderId === id ? { ...s, lat, lng } : s)) }
                : r,
            ),
          };
        });
        setRevision((r) => r + 1);
        return;
      }
      window.clearTimeout(orderTimer);
      orderTimer = window.setTimeout(reload, 400);
    });

    /*
     * A finished build reloads first and reports second, so whatever shows
     * "done" is looking at the new routes when it says so.
     */
    source.addEventListener('optimize.finished', (event) => {
      const data = JSON.parse((event as MessageEvent).data) as {
        requestId: string;
        stats?: Extract<OptimizeEvent, { type: 'finished' }>['stats'];
      };
      void refresh()
        .catch(() => {})
        .finally(() => onOptimizeRef.current?.({ type: 'finished', requestId: data.requestId, stats: data.stats ?? null }));
    });
    source.addEventListener('optimize.failed', (event) => {
      const data = JSON.parse((event as MessageEvent).data) as { requestId: string; error: string };
      onOptimizeRef.current?.({ type: 'failed', requestId: data.requestId, error: data.error });
    });

    source.addEventListener('position', (event) => {
      const payload = JSON.parse((event as MessageEvent).data) as {
        position: Board['positions'][number];
      };
      setBoard((current) => {
        if (!current) return current;
        const key = payload.position.vehicleId ?? payload.position.technicianId;
        const positions = current.positions.filter(
          (p) => (p.vehicleId ?? p.technicianId) !== key,
        );
        return { ...current, positions: [...positions, payload.position] };
      });
    });

    source.onerror = () => {
      // EventSource reconnects on its own; surface the gap rather than tearing down.
      setConnection((c) => (c === 'live' ? 'error' : 'connecting'));
    };

    return () => {
      window.clearTimeout(orderTimer);
      source.close();
      sourceRef.current = null;
    };
  }, [territoryId, date, refresh]);

  return { board, connection, revision, lastEventAt, refresh };
}

function applyProjection(
  setBoard: React.Dispatch<React.SetStateAction<Board | null>>,
  projection: RouteProjection,
): void {
  setBoard((current) => {
    if (!current) return current;
    const index = current.routes.findIndex((r) => r.id === projection.routeId);
    if (index === -1) return current;

    const route = current.routes[index] as BoardRoute;
    const byId = new Map(projection.stops.map((s) => [s.id, s]));

    const stops: BoardStop[] = route.stops.map((stop) => {
      const next = byId.get(stop.id);
      if (!next) return stop;
      return {
        ...stop,
        projectedArrival: next.arrival,
        projectedDeparture: next.departure,
        deltaSeconds: next.deltaSeconds,
        windowRisk: next.windowRisk,
        status: next.status,
        // Observed values are only ever set by the server; a projection that
        // says "not a forecast" means these are now facts.
        actualArrival: next.isProjected ? stop.actualArrival : next.arrival,
        actualDeparture: next.isProjected ? stop.actualDeparture : next.departure,
      };
    });

    const routes = [...current.routes];
    routes[index] = {
      ...route,
      stops,
      travelFactor: projection.travelFactor,
      projectedEnd: projection.projectedRouteEnd,
      deltaSeconds: projection.routeDeltaSeconds,
    };
    return { ...current, routes };
  });
}
